import type { RadioStation } from '@/mocks/radios';
import { topFans, type TopFan } from '@/mocks/topFans';

interface RadioTopFansSectionProps {
  station: RadioStation;
}

const stationFanMapping: Record<number, string> = {
  0: 'Radio Éxito FM',
  1: 'Radio Éxito FM',
  2: 'Estéreo Capital',
  3: 'Radio Deportiva Total',
  5: 'Radio Industria',
  7: 'Radio Deportiva Total',
  9: 'Radio Éxito FM',
  12: 'Radio Peninsular',
  14: 'Radio Élite Digital',
  16: 'Radio Peninsular',
  19: 'Radio Deportiva Total',
};

export default function RadioTopFansSection({ station }: RadioTopFansSectionProps) {
  const mappedRadioName = stationFanMapping[station.id];

  if (mappedRadioName) {
    const stationFans = topFans
      .filter((f) => f.radioName === mappedRadioName)
      .sort((a, b) => (b.totalScore || 0) - (a.totalScore || 0))
      .slice(0, 10);

    if (stationFans.length > 0) {
      return <FanGrid fans={stationFans} stationName={station.name} />;
    }
  }

  const fallbackFans = topFans.filter((f) => f.radioName === 'Radio Éxito FM').slice(0, 10);
  return <FanGrid fans={fallbackFans} stationName={station.name} />;
}

function FanGrid({ fans, stationName }: { fans: TopFan[]; stationName: string }) {
  const formatNumber = (n: number) => {
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
    return n.toString();
  };

  const badgeColors: Record<string, { bg: string; icon: string; text: string }> = {
    oro: { bg: 'bg-amber-400', icon: 'ri-medal-fill', text: 'text-amber-800' },
    plata: { bg: 'bg-foreground-300', icon: 'ri-medal-fill', text: 'text-foreground-600' },
    bronce: { bg: 'bg-orange-400', icon: 'ri-medal-fill', text: 'text-orange-800' },
    ninguno: { bg: 'bg-background-200/70', icon: 'ri-heart-fill', text: 'text-foreground-500' },
  };

  return (
    <section className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3 border-b border-background-200/70">
        <h3 className="text-base font-bold text-foreground-950 uppercase tracking-wide">Top Fans</h3>
        <div className="flex items-center gap-1 text-xs text-foreground-500">
          <i className="ri-trophy-line"></i>
          Top 10
        </div>
      </div>

      {fans.length >= 3 && (
        <div className="p-5 flex items-end justify-center gap-4 pb-0">
          {fans.slice(0, 3).map((fan, i) => {
            const order = [1, 0, 2];
            const rank = order[i];
            const heights = ['h-28', 'h-36', 'h-24'];
            return (
              <div key={fan.id} className="flex flex-col items-center gap-2 min-w-[80px]">
                <div className="relative">
                  <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-background-200/70 overflow-hidden border-2 border-background-100">
                    <img
                      src={fan.photoUrl}
                      alt={fan.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className={`absolute -bottom-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center text-white text-[10px] font-bold ${
                    fan.rank === 1 ? 'bg-amber-400' : fan.rank === 2 ? 'bg-foreground-400' : 'bg-orange-400'
                  }`}>
                    {fan.rank}
                  </div>
                </div>
                <div className={`${heights[rank]} w-full rounded-lg bg-gradient-to-t ${
                  fan.rank === 1 ? 'from-amber-100 to-background-50' : fan.rank === 2 ? 'from-foreground-100 to-background-50' : 'from-orange-100 to-background-50'
                } flex flex-col items-center justify-center pt-2`}>
                  <p className="text-xs font-bold text-foreground-950 line-clamp-1 text-center">{fan.name}</p>
                  <p className="text-[10px] text-foreground-500">@{fan.username}</p>
                  <p className="text-xs font-bold text-foreground-700 mt-1">{fan.totalScore} pts</p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="p-4 pt-3 divide-y divide-background-200/70">
        <div className="grid grid-cols-2 gap-2 mt-2">
          {fans.slice(3, 7).map((fan) => (
            <FanListItem key={fan.id} fan={fan} formatNumber={formatNumber} badgeColors={badgeColors} />
          ))}
        </div>
        {fans.length > 7 && (
          <div className="grid grid-cols-2 gap-2 mt-2 pt-2">
            {fans.slice(7, 10).map((fan) => (
              <FanListItem key={fan.id} fan={fan} formatNumber={formatNumber} badgeColors={badgeColors} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function FanListItem({ fan, formatNumber, badgeColors }: { fan: TopFan; formatNumber: (n: number) => string; badgeColors: Record<string, { bg: string; icon: string; text: string }> }) {
  const badge = badgeColors[fan.badge] || badgeColors.ninguno;

  return (
    <div className="flex items-center gap-2 p-2 rounded-lg hover:bg-background-100 transition-colors cursor-pointer">
      <div className="relative flex-shrink-0">
        <div className="w-9 h-9 rounded-full bg-background-200/70 overflow-hidden">
          <img
            src={fan.photoUrl}
            alt={fan.name}
            className="w-full h-full object-cover"
          />
        </div>
        <div className={`absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full flex items-center justify-center text-white text-[8px] font-bold ${
          fan.rank <= 3 ? badge.bg : 'bg-foreground-200/70'
        }`}>
          {fan.rank}
        </div>
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-semibold text-foreground-950 truncate">{fan.name}</p>
        <div className="flex items-center gap-2 text-[10px] text-foreground-500">
          <span className="flex items-center gap-0.5"><i className="ri-time-line text-[10px]"></i> {fan.hoursListened}h</span>
          <span>{fan.totalScore} pts</span>
        </div>
      </div>
    </div>
  );
}